import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { useIngredientUsage, useDeleteIngredient } from '@/features/ingredients/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';

export const Route = createFileRoute('/_authed/ingredients/')({
  component: IngredientsPage,
});

const LIMIT = 50;

function IngredientsPage() {
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const trimmed = query.trim();
  const { data, isLoading } = useIngredientUsage({
    search: trimmed || undefined,
    limit: LIMIT,
    offset,
  });
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const navigate = useNavigate();

  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null);
  const del = useDeleteIngredient();

  const onConfirmDelete = async () => {
    if (!toDelete) return;
    await del.mutateAsync({ id: toDelete.id });
    setToDelete(null);
  };

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setOffset(0);
  };

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + LIMIT, total);

  return (
    <div className="p-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Ingredients</h1>
          {!isLoading && (
            <p className="text-ink-soft text-sm mt-1">
              {total} ingredient{total === 1 ? '' : 's'}
            </p>
          )}
        </div>
        <Button onClick={() => navigate({ to: '/ingredients/new' })}>
          <Plus size={16} /> New ingredient
        </Button>
      </header>

      <div className="mb-4 max-w-md">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
            <Search size={16} />
          </div>
          <Input
            placeholder="Search by name or alias…"
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-ink-soft">
            {trimmed ? 'No matches.' : 'No ingredients yet.'}
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                  style={{ borderColor: 'var(--color-hairline)' }}
                >
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-right">Appearances</th>
                  <th className="px-4 py-3">Rich data</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-b last:border-b-0 hover:bg-cream-deep/60"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.displayName}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink-soft">
                      {row.recipeCount}
                    </td>
                    <td className="px-4 py-3">
                      {row.ingredientInfoId != null ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Badge tone="sage">Rich</Badge>
                          <span className="text-xs text-ink-soft">
                            · {row.aliasCount} {row.aliasCount === 1 ? 'alias' : 'aliases'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {row.ingredientInfoId != null ? (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                navigate({
                                  to: '/ingredients/$id/edit',
                                  params: { id: row.ingredientInfoId! },
                                })
                              }
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-soft hover:bg-honey-pale hover:text-ink"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setToDelete({
                                  id: row.ingredientInfoId!,
                                  name: row.displayName,
                                })
                              }
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-soft hover:bg-[var(--color-error-bg)] hover:text-danger"
                              title="Delete rich data"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              navigate({
                                to: '/ingredients/new',
                                search: { name: row.displayName },
                              })
                            }
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-soft hover:bg-honey-pale hover:text-ink"
                            title="Create rich data"
                          >
                            <Plus size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > LIMIT && (
              <div
                className="flex items-center justify-between px-4 py-3 border-t text-sm text-ink-soft"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <span>
                  Showing {rangeStart}–{rangeEnd} of {total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset + LIMIT >= total}
                    onClick={() => setOffset(offset + LIMIT)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Dialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title="Delete ingredient rich data?"
      >
        <p className="text-ink-soft text-sm">
          This will permanently delete the rich data for{' '}
          <strong className="text-ink">{toDelete?.name}</strong>, including all aliases and
          alternatives. Recipes that reference this ingredient by name are unaffected. This
          cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirmDelete} disabled={del.isPending}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
